import brownPelican from "@/assets/birds/brown-pelican.jpg";
import westernGull from "@/assets/birds/western-gull.jpg";
import heermannsGull from "@/assets/birds/heermanns-gull.jpg";
import snowyEgret from "@/assets/birds/snowy-egret.jpg";
import snowyPlover from "@/assets/birds/snowy-plover.jpg";
import marbledGodwit from "@/assets/birds/marbled-godwit.jpg";
import sanderling from "@/assets/birds/sanderling.jpg";
import blackOystercatcher from "@/assets/birds/black-oystercatcher.jpg";
import elegantTern from "@/assets/birds/elegant-tern.jpg";
import brandtsCormorant from "@/assets/birds/brandts-cormorant.jpg";
import osprey from "@/assets/birds/osprey.jpg";
import blackSkimmer from "@/assets/birds/black-skimmer.jpg";
import peregrineFalcon from "@/assets/birds/peregrine-falcon.jpg";

export type BirdSeason = "Year-round" | "Winter visitor" | "Summer breeder";
export type BirdHabitat = "Sand" | "Cliffs" | "Tidepools" | "Lagoon" | "Open ocean" | "Pier";

export interface CoastalBird {
  id: string;
  commonName: string;
  sciName: string;
  blurb: string;
  whereToSee: string;
  season: BirdSeason;
  habitats: BirdHabitat[];
  image: string;
}

export const coastalBirds: CoastalBird[] = [
  {
    id: "brown-pelican",
    commonName: "Brown Pelican",
    sciName: "Pelecanus occidentalis",
    blurb: "The unmistakable icon of the California coast — squadrons glide inches above the swell in perfect single file before plunging headlong after anchovies.",
    whereToSee: "Anywhere along the open coast; reliable from any pier.",
    season: "Year-round",
    habitats: ["Open ocean", "Pier"],
    image: brownPelican,
  },
  {
    id: "western-gull",
    commonName: "Western Gull",
    sciName: "Larus occidentalis",
    blurb: "The big, bold local — pure white head, slate-grey back, and a yellow bill with a red dot. The boardwalk regular that will absolutely steal your sandwich.",
    whereToSee: "Every beach, every parking lot, every pier railing.",
    season: "Year-round",
    habitats: ["Sand", "Pier", "Cliffs"],
    image: westernGull,
  },
  {
    id: "heermanns-gull",
    commonName: "Heermann's Gull",
    sciName: "Larus heermanni",
    blurb: "Easily the most beautiful gull on the coast — slate-grey body, snow-white head, and a vivid red bill. A post-breeding visitor from islands off Baja.",
    whereToSee: "Mixed in with Western Gulls, July through April.",
    season: "Winter visitor",
    habitats: ["Sand", "Pier"],
    image: heermannsGull,
  },
  {
    id: "snowy-egret",
    commonName: "Snowy Egret",
    sciName: "Egretta thula",
    blurb: "Brilliant white plumes, a dagger-black bill, and unmistakable yellow 'golden slippers' — stalks small fish in the shallows with a slow, deliberate prowl.",
    whereToSee: "San Elijo Lagoon, Tijuana Estuary, Children's Pool tide pools.",
    season: "Year-round",
    habitats: ["Lagoon", "Tidepools"],
    image: snowyEgret,
  },
  {
    id: "snowy-plover",
    commonName: "Western Snowy Plover",
    sciName: "Charadrius nivosus",
    blurb: "A federally threatened, palm-sized shorebird that nests in shallow scrapes right on the open sand. If you see roped-off areas in spring, this is who they protect.",
    whereToSee: "Coronado, Silver Strand, and the north end of Imperial Beach.",
    season: "Year-round",
    habitats: ["Sand"],
    image: snowyPlover,
  },
  {
    id: "marbled-godwit",
    commonName: "Marbled Godwit",
    sciName: "Limosa fedoa",
    blurb: "A large cinnamon-buff shorebird with an absurdly long, slightly upturned bicolored bill — works the wet sand in deliberate, methodical sweeps.",
    whereToSee: "Mission Bay flats, Cardiff Reef, and any wide low-tide beach.",
    season: "Winter visitor",
    habitats: ["Sand", "Lagoon"],
    image: marbledGodwit,
  },
  {
    id: "sanderling",
    commonName: "Sanderling",
    sciName: "Calidris alba",
    blurb: "The wave-chasing wind-up toys — small flocks sprint up the beach as each wave recedes, probing for sand crabs in the seconds before the next set arrives.",
    whereToSee: "Every flat sandy beach in winter; especially Coronado and Pacific Beach.",
    season: "Winter visitor",
    habitats: ["Sand"],
    image: sanderling,
  },
  {
    id: "black-oystercatcher",
    commonName: "Black Oystercatcher",
    sciName: "Haematopus bachmani",
    blurb: "All-black plumage, a fire-engine red bill, and pink legs — a noisy, conspicuous specialist of the rocky intertidal that pries limpets and mussels off the rocks.",
    whereToSee: "La Jolla cliffs, Cabrillo tide pools, Sunset Cliffs.",
    season: "Year-round",
    habitats: ["Tidepools", "Cliffs"],
    image: blackOystercatcher,
  },
  {
    id: "elegant-tern",
    commonName: "Elegant Tern",
    sciName: "Thalasseus elegans",
    blurb: "Slim, fast, and aptly named — long orange bill, shaggy black crest. Hovers above the surf line and plunges for grunion and anchovies in spring and summer.",
    whereToSee: "Open coast and bay edges, March through October.",
    season: "Summer breeder",
    habitats: ["Open ocean", "Lagoon"],
    image: elegantTern,
  },
  {
    id: "brandts-cormorant",
    commonName: "Brandt's Cormorant",
    sciName: "Urile penicillatus",
    blurb: "Glossy black seabirds that nest shoulder-to-shoulder on the cliff ledges — in breeding season the throat patch turns electric cobalt blue.",
    whereToSee: "La Jolla Cove cliffs and the rocks below Children's Pool.",
    season: "Year-round",
    habitats: ["Cliffs", "Open ocean"],
    image: brandtsCormorant,
  },
  {
    id: "osprey",
    commonName: "Osprey",
    sciName: "Pandion haliaetus",
    blurb: "A fish-hawk with a six-foot wingspan — hovers high over open water, then folds and crashes feet-first to grab fish in its talons.",
    whereToSee: "Mission Bay, San Diego River mouth, San Elijo Lagoon.",
    season: "Year-round",
    habitats: ["Lagoon", "Open ocean"],
    image: osprey,
  },
  {
    id: "black-skimmer",
    commonName: "Black Skimmer",
    sciName: "Rynchops niger",
    blurb: "Unmistakable — flies inches above the water with its longer lower mandible slicing the surface, snapping shut on anything it touches. A South Bay specialty.",
    whereToSee: "South San Diego Bay and the salt works at sunset.",
    season: "Summer breeder",
    habitats: ["Lagoon", "Open ocean"],
    image: blackSkimmer,
  },
  {
    id: "peregrine-falcon",
    commonName: "Peregrine Falcon",
    sciName: "Falco peregrinus",
    blurb: "The fastest animal on earth — stoops from hundreds of feet up at over 200 mph to take pigeons and shorebirds in mid-air. Once nearly wiped out by DDT, now nesting again on San Diego's sandstone bluffs.",
    whereToSee: "Torrey Pines State Reserve cliffs, Sunset Cliffs, La Jolla bluffs.",
    season: "Year-round",
    habitats: ["Cliffs", "Open ocean"],
    image: peregrineFalcon,
  },
];
